<template>
    <v-sheet
        class="d-flex flex-column"
    >
        <AmortizationBackToolbar
            v-if="amortization.multiple.documents.length"
            :page="2"
            @back="$emit('back')"
        />

        <div class="overflow-y-auto">
            <section class="px-4 pb-10">
                <div class="text-subtitle-1 bold mb-2">{{ translate('common.select_number_of_months') }}</div>
                <div class="text-body-2 text-medium-emphasis">
                    {{
                        translate('common.creditor_allows_amortization_x_months', [
                            creditor_name,
                            max_number_of_advices,
                        ])
                    }}
                </div>
            </section>
            <section>
                <v-slider
                    v-model="number_of_months"
                    :max="max_number_of_advices"
                    :min="1"
                    :step="1"
                    thumb-label="always"
                >
                    <template #prepend>
                        <v-btn
                            @click="decrement"
                            icon="mdi-minus"
                            size="small"
                            variant="icon"
                        />
                    </template>
                    <template #append>
                        <v-btn
                            @click="increment"
                            icon="mdi-plus"
                            size="small"
                            variant="icon"
                        />
                    </template>
                </v-slider>
            </section>
            <section class="px-4">
                <div class="d-flex align-center justify-space-between">
                    <div class="text-subtitle-1 bold">{{ translate('common.monthly_cost') }}</div>
                    <v-progress-circular
                        v-if="loading"
                        :size="36"
                        indeterminate
                    />
                    <div
                        v-else
                        class="text-h5"
                    >
                        {{ format_as_currency(amortization_details['amount_per_advice'], currency) }}
                    </div>
                </div>
                <div class="text-caption text-medium-emphasis text-right mb-2">
                    {{ translate('common.monthly_cost_footnote') }}
                </div>
                <div class="text-center mb-4">
                    <v-btn
                        @click="expanded = !expanded"
                        size="x-small"
                        variant="text"
                    >
                        <template #append>
                            <v-icon
                                v-if="!expanded"
                                icon="mdi-chevron-down"
                            />
                            <v-icon
                                v-else
                                icon="mdi-chevron-up"
                            />
                        </template>
                        {{ translate('common.how_did_we_count') }}
                    </v-btn>
                </div>
            </section>
            <section class="px-4 flex-grow-1">
                <v-expand-transition>
                    <div v-show="expanded">
                        <div class="text-subtitle-1 bold mb-4">{{ translate('common.details') }}</div>
                        <AmortizationText
                            :label="debts_count"
                            :value="format_as_currency(amortization_details['total_debt'], currency)"
                        />
                        <template v-if="Object.keys(amortization_details['simulated_additional_debt'] || {}).length">
                            <div class="d-flex justify-space-between mb-4">
                                <div class="text-body-2 bold">{{ translate('common.additional_costs_and_interest') }}</div>
                                <div class="text-body-2 bold">
                                    {{
                                        format_as_currency(sum(Object.values(amortization_details['simulated_additional_debt'])), currency)
                                    }}
                                </div>
                            </div>

                            <AmortizationText
                                v-for="[transaction_type, amount] in Object.entries(amortization_details['simulated_additional_debt'])"
                                :key="`simulated_additional_debt_${transaction_type}`"
                                :label="translate(`transactions.${transaction_type}`)"
                                :value="format_as_currency(amount, currency)"
                            />
                        </template>
                        <template
                            v-if="Object.keys(amortization_details['transferable_additional_debt_on_create'] || {}).length"
                        >
                            <div class="d-flex justify-space-between mb-4">
                                <div class="text-body-2 bold">{{ translate('common.transferred_costs') }}</div>
                                <div class="text-body-2 bold">
                                    {{
                                        format_as_currency(sum(Object.values(amortization_details['transferable_additional_debt_on_create'])), currency)
                                    }}
                                </div>
                            </div>

                            <AmortizationText
                                v-for="[transaction_type, amount] in Object.entries(amortization_details['transferable_additional_debt_on_create'])"
                                :key="`transferable_additional_debt_on_create_${transaction_type}`"
                                :label="translate(`transactions.${transaction_type}`)"
                                :value="format_as_currency(amount, currency)"
                            />
                        </template>
                        <div class="d-flex justify-space-between mb-4">
                            <div class="text-body-1 bold">{{ translate('common.total') }}</div>
                            <div class="text-body-1 bold">
                                {{ format_as_currency(amortization_details['total_amortization_amount'], currency) }}
                            </div>
                        </div>

                    </div>
                </v-expand-transition>
                <div class="text-subtitle-1 bold mb-4">{{ translate('common.amortization_information') }}</div>
                <AmortizationText
                    :label="translate('common.first_payment_date')"
                    :value="first_payment_date"
                />
                <div class="text-body-2">
                    {{ translate('common.advices_information') }}
                </div>
            </section>
        </div>

        <v-btn
            :disabled="loading"
            @click="start_amortization"
            class="mx-4 mt-4"
            color="primary"
        >
            {{ translate('common.split') }}
        </v-btn>
        <div class="ma-4 text-caption text-medium-emphasis">
            {{
                translate('common.amortization_start_information', [
                    format_as_currency(amortization_details["setup_fee"], currency),
                    format_as_currency(amortization_details["advice_fee"], currency),
                ])
            }}
        </div>
    </v-sheet>
</template>

<script>
import { locales_mixin } from '@old/mixins/locales_mixin'
import { mapState } from 'vuex'
import { api_mixin } from '@old/mixins/api_mixin'
import { date_mixin } from '@old/mixins/date_mixin'
import AmortizationText from '@old/components/amortization/AmortizationText.vue'
import { add, getDaysInMonth, lastDayOfMonth } from 'date-fns'
import AmortizationBackToolbar from '@old/components/amortization/AmortizationBackToolbar.vue'
import { utils_mixin } from '@old/mixins/utils_mixin'

export default {
    name: 'AmortizationSetup',
    props: {},
    mixins: [api_mixin, date_mixin, locales_mixin, utils_mixin],
    components: { AmortizationBackToolbar, AmortizationText },
    emits: ['confirm', 'back'],
    data() {
        return {
            loading: true,
            interest: null,
            number_of_months: 1,
            expanded: false,
            amortization_details: {},
        }
    },
    computed: {
        payload() {
            const payload = {
                amortization_date: new Date(this.first_payment_date).toUTCString(),
                number_of_advices: this.number_of_months,
                cases: [],
                debt_collections: [],
                enforcements: [],
                send_first_advice_letter_only: false,
                skip_amortization_setup_fee: false,
            }

            const documents = []
            if (this.documents.length) {
                documents.push(...this.documents)
            } else {
                documents.push(this.document)
            }

            for (const document of documents) {
                const id = document['_id']
                if (document.reference === 'case') {
                    payload['cases'].push({ id })
                } else if (document.reference === 'debt_collection') {
                    payload['debt_collections'].push({ id })
                }
            }

            return payload
        },
        document() {
            if (Object.keys(this.amortization.document).length) {
                return this.amortization.document
            } else {
                return this.amortization.multiple.documents.find((v) => {
                    return this.amortization.multiple.selected.includes(v['_id'])
                })
            }
        },
        documents() {
            return this.amortization.multiple.documents.filter((v) => {
                return this.amortization.multiple.selected.includes(v['_id'])
            })
        },
        creditor_name() {
            return this.deep_get(this.document, 'creditor_name')
        },
        currency() {
            return this.deep_get(this.document, 'currency')
        },
        max_number_of_advices() {
            let max_number_of_advices = this.amortization.params['max_number_of_advices'] || 0
            if (this.amortization.params['max_number_of_advices_intervals']) {
                const intervals = JSON.parse(
                    JSON.stringify(this.amortization.params['max_number_of_advices_intervals']),
                )
                const matched_interval = intervals.find((x) => {
                    if (x.from_amount <= this.amortization_details["total_debt"] && this.amortization_details["total_debt"] <= x.to_amount) {
                        return x
                    }
                })
                max_number_of_advices = matched_interval.max_number_of_advices
            }

            return max_number_of_advices
        },
        first_payment_date() {
            const today = new Date()
            const advice_date_days_before_due_date = this.amortization.params['advice_date_days_before_due_date']
            if (today.getDate() <= getDaysInMonth(today) - advice_date_days_before_due_date) {
                return this.format_date(lastDayOfMonth(today))
            } else {
                return this.format_date(lastDayOfMonth(add(today, { months: 1 })))
            }
        },
        debts_count() {
            if (!this.documents.length) {
                return this.translate('common.debt')
            } else {
                return `${this.translate('common.debts')} (${this.documents.length})`
            }
        },
        ...mapState(['amortization']),
    },
    watch: {
        payload: {
            handler(val) {
                if (val) {
                    this.get_amortization_details()
                }
            },
            immediate: true,
        },
    },
    methods: {
        async get_amortization_details() {
            if (
                this.payload &&
                (this.payload.cases.length || this.payload.debt_collections.length || this.payload.enforcements.length) &&
                this.payload.number_of_advices > 0
            ) {
                const response = await this.api_post({
                    url: '/customer--amortization/details-new-amortization',
                    data: this.payload,
                })
                this.amortization_details = response.data
            } else {
                this.amortization_details = {}
            }
        },
        decrement() {
            if (this.number_of_months !== 1) this.number_of_months--
        },
        increment() {
            if (this.number_of_months !== this.max_number_of_advices) this.number_of_months++
        },
        start_amortization() {
            this.$emit('confirm', this.payload)
        },
        sum(values) {
            let total = 0
            for (const amount of values) {
                total += amount
            }
            return total
        },
    },
    beforeCreate() {
    },
    created() {
    },
    beforeMount() {
    },
    async mounted() {
        this.number_of_months = this.max_number_of_advices
        this.loading = false
    },
    beforeUpdate() {
    },
    updated() {
    },
    beforeUnmount() {
    },
}
</script>

<style scoped lang="sass"></style>
